import { invalidateInventory } from '../collection/inventoryInvalidation';
import { useEffect, useRef } from 'react';
import { InventoryAuthority } from '@qkindex/contracts';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import { itemService } from '../../services/itemService';
import { logger } from '../../services/logger';
import { useFeedback } from '../feedback/FeedbackProvider';
import { createOperationId } from '../../domain/operationId';
import type {
  ItemEditEnqueue,
  ItemEditOptions,
} from '../rapidCapture/rapidCaptureTypes';

export function useItemActions({
  authority,
  deletionAuthority = authority,
  items,
  locations,
  keywords,
  location,
  qty,
  image,
  imageBlob,
  removeExistingImage,
  sourceItemId,
  tagIds,
  resetForm,
  setLastUsedLocation,
  setSelectedItem,
  setItems,
  closeAdd,
  closeDetails,
  onCapacityChanged,
  onCreated,
  enqueueEdit,
  editingItem,
}: {
  authority?: InventoryAuthority;
  deletionAuthority?: InventoryAuthority;
  items: Item[];
  locations: Location[];
  keywords: string;
  location: string;
  qty: number | string;
  image: string | null;
  imageBlob: Blob | null;
  removeExistingImage: boolean;
  sourceItemId?: string;
  tagIds?: string[];
  resetForm: (nextLocation?: string) => void;
  setLastUsedLocation: (location: string) => Promise<void> | void;
  setSelectedItem: (item: Item | null) => void;
  setItems: React.Dispatch<React.SetStateAction<Item[]>>;
  closeAdd: () => void;
  closeDetails: () => void;
  onCapacityChanged?: () => void;
  onCreated?: (itemId: string) => void;
  enqueueEdit?: ItemEditEnqueue;
  editingItem?: Item | null;
}) {
  const { notify } = useFeedback();
  const tagKey = JSON.stringify(tagIds);
  const pendingCreateOperationId = useRef<string | undefined>(undefined);
  useEffect(() => {
    pendingCreateOperationId.current = undefined;
  }, [
    keywords,
    location,
    qty,
    image,
    imageBlob,
    sourceItemId,
    tagKey,
    authority?.collectionId,
    authority?.userId,
  ]);
  const numericQty = () => Number(qty);
  const create = async () => {
    if (!authority || !location.trim()) return false;
    const operationId = pendingCreateOperationId.current || createOperationId();
    pendingCreateOperationId.current = operationId;
    const itemId = await itemService.createItem(
      authority,
      {
        keywords,
        ...(tagIds === undefined ? {} : { tagIds }),
        location,
        locationId: locations.find((candidate) => candidate.name === location)
          ?.id,
        qty: numericQty(),
        image: imageBlob,
        sourceItemId: !imageBlob ? sourceItemId : undefined,
      },
      operationId,
    );
    pendingCreateOperationId.current = undefined;
    invalidateInventory({
      ...authority,
      resources: ['items'],
      reloadItemIds: [itemId],
    });
    onCreated?.(itemId);
    onCapacityChanged?.();
    await setLastUsedLocation(location);
    return true;
  };
  const report = async (
    action: () => Promise<void | boolean>,
    message: string,
    capacitySensitive = false,
  ): Promise<boolean> => {
    try {
      return (await action()) !== false;
    } catch (error) {
      logger.error(message, error);
      const cause = (error as { cause?: unknown })?.cause;
      const contractCode =
        (error as { details?: { code?: unknown } })?.details?.code ||
        (cause as { details?: { code?: unknown } })?.details?.code;
      if (capacitySensitive && contractCode === 'capacity-exceeded') {
        onCapacityChanged?.();
        notify(
          'This collection no longer has an item unit available. Your draft is still open.',
          'warning',
        );
        return false;
      }
      if (contractCode === 'operation-conflict') {
        notify(
          'This item changed. Reopen it before saving your changes.',
          'warning',
        );
        return false;
      }
      notify(
        (error as { code?: string })?.code === 'mutation-pending'
          ? (error as Error).message
          : message,
        'error',
      );
      return false;
    }
  };

  return {
    saveAndAddAnother: () =>
      report(
        async () => {
          if (!(await create())) return false;
          resetForm(location);
          return true;
        },
        'The item could not be saved.',
        true,
      ),
    saveAndClose: () =>
      report(
        async () => {
          if (!(await create())) return false;
          resetForm();
          closeAdd();
          return true;
        },
        'The item could not be saved.',
        true,
      ),
    update: (itemId: string, options?: ItemEditOptions) =>
      report(async () => {
        if (!authority || !location.trim()) return false;
        const original =
          editingItem || items.find((item) => item.id === itemId);
        const destination = locations.find(
          (candidate) => candidate.name === location,
        );
        if (!original || !destination)
          throw new Error(
            'The item or location is unavailable. Reopen it before saving.',
          );
        const fields = {
          keywords,
          location,
          locationId: destination.id,
          qty: numericQty(),
          ...(tagIds === undefined ? {} : { tagIds }),
        };
        const changes =
          options?.reviewedRevision === undefined
            ? Object.fromEntries(
                Object.entries(fields).filter(
                  ([key, value]) =>
                    JSON.stringify(value) !==
                    JSON.stringify(original[key as keyof Item]),
                ),
              )
            : fields;
        if (Object.keys(changes).length || removeExistingImage || imageBlob) {
          if (!enqueueEdit)
            throw new Error(
              'The local save queue is unavailable. Reopen the item and try again.',
            );
          await enqueueEdit(
            original,
            changes,
            removeExistingImage ? null : imageBlob || undefined,
            options,
          );
        }
        void Promise.resolve(setLastUsedLocation(location)).catch(
          () => undefined,
        );
        setSelectedItem(null);
        closeDetails();
        resetForm();
        return true;
      }, 'The item could not be updated.'),
    moveToBin: (itemId: string) =>
      report(async () => {
        if (!deletionAuthority || !items.some((item) => item.id === itemId))
          return false;
        await itemService.moveItemToBin(deletionAuthority, itemId);
        invalidateInventory({
          ...deletionAuthority,
          resources: ['items'],
          reloadItemIds: [itemId],
        });
        setItems((current) => current.filter((item) => item.id !== itemId));
        return true;
      }, 'The item could not be moved to the bin.'),
  };
}
